import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearCredentials, setCredentials } from "./authSlice";

const baseUrl =
  process.env.REACT_APP_API_BASE_URL || "https://stagingapi.acadimyst.com/";

// Main base query — attaches Bearer token from Redux + sends httpOnly cookie
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Refresh base query — NO Authorization header, credentials: include
const baseQueryForRefresh = fetchBaseQuery({
  baseUrl,
  credentials: "include",
});

// Simple mutex: deduplicate concurrent refresh calls
let refreshPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Pre-flight: reject immediately if the max session window has elapsed
  const maxSessionUntil = api.getState().auth.maxSessionUntil;
  if (maxSessionUntil && new Date() >= new Date(maxSessionUntil)) {
    api.dispatch(clearCredentials());
    return { error: { status: 401, data: "Session expired" } };
  }

  let result = await baseQueryWithAuth(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // Start a single refresh; concurrent 401s share the same promise
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          return await baseQueryForRefresh(
            {
              url: "api/v1/en/student/auth/refresh-token",
              method: "POST",
              body: {},
            },
            api,
            extraOptions
          );
        } finally {
          refreshPromise = null;
        }
      })();
    }

    const refreshResult = await refreshPromise;

    if (refreshResult?.data) {
      const newToken = refreshResult.data?.data?.data?.token;
      const newMaxSessionUntil =
        refreshResult.data?.data?.data?.maxSessionUntil;
      if (newToken) {
        api.dispatch(
          setCredentials({ token: newToken, maxSessionUntil: newMaxSessionUntil })
        );
        // Retry the original request with the refreshed token
        result = await baseQueryWithAuth(args, api, extraOptions);
      } else {
        // Refresh returned no token — session gone (e.g. login from another device).
        // Clear creds + drop cached queries so ProtectedRoute redirects cleanly
        // and the login page doesn't render with stale data.
        api.dispatch(clearCredentials());
        api.dispatch(apiSlice.util.resetApiState());
      }
    } else {
      // Refresh itself errored → same treatment.
      api.dispatch(clearCredentials());
      api.dispatch(apiSlice.util.resetApiState());
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: [
    "Login",
    "ListeningTests",
    "StudentPackages",
    "MockTests",
    "MiniMockTests",
    "ClassTests",
  ],
  endpoints: () => ({}),
});